import { useEffect, useRef, useState, useCallback, } from "react";
import getCookie from "../components/LanguageProvider/utils/cookies";
import { removeFbclidCookie } from "../utils/UTM";
import { parseConsentCookie } from "../utils/cookieConsent";
import { refreshRegionCookie } from "../utils/regionConsent";
import safely from "../utils/safeCookie";
const REGION_COOKIE = "cookie_consent_region_restriction";
export default function useCookieConsent() {
    const [consent, setConsent] = useState(null);
    const hasRequestedRegionCookie = useRef(false);
    const isMounted = useRef(false);
    const removeFunctionalCookies = useCallback(() => {
        ["movement", "language"].forEach((cookieName) => {
            document.cookie = `${cookieName}=; max-age=0; path=/`;
        });
    }, []);
    const applyConsentToAll = useCallback(() => {
        setConsent({
            action: "yes",
            advertisement: "yes",
            analytics: "yes",
            consent: "yes",
            functional: "yes",
            necessary: "yes",
            other: "yes",
            performance: "yes",
        });
    }, []);
    const denyConsentToAll = useCallback(() => {
        setConsent({
            action: "no",
            advertisement: "no",
            analytics: "no",
            consent: "no",
            functional: "no",
            necessary: "no",
            other: "no",
            performance: "no",
        });
    }, []);
    const processConsent = useCallback(() => {
        const cookie = safely(() => getCookie("cookieyes-consent"), null);
        if (!cookie) {
            const regionCookie = safely(() => getCookie(REGION_COOKIE), null);
            // Fails safe while unresolved: only a confirmed "false" (visitor is known not to be in
            // the UK/EU) grants consent by default. A missing region cookie is treated the same as
            // "true" (deny) rather than assuming it's safe - this can happen on a cold cache hit
            // before the region cookie has had a chance to be set (SetRegionRestrictionCookieMiddleware
            // is global 'web' middleware, so a CDN cache hit skips it entirely). Request an uncached
            // round trip once to get it set, then re-resolve - rather than denying for the rest of the
            // visit a visitor the server would have confirmed as non-UK/EU.
            if (regionCookie === null && !hasRequestedRegionCookie.current) {
                hasRequestedRegionCookie.current = true;
                safely(removeFbclidCookie, undefined);
                denyConsentToAll();
                refreshRegionCookie().finally(() => {
                    if (isMounted.current) {
                        processConsent();
                    }
                });
                return;
            }
            if (regionCookie === "false") {
                applyConsentToAll();
            }
            else {
                safely(removeFbclidCookie, undefined);
                denyConsentToAll();
            }
            return;
        }
        const parsed = parseConsentCookie(cookie);
        if (parsed.functional === "no") {
            safely(removeFunctionalCookies, undefined);
        }
        if (parsed.analytics !== "yes") {
            safely(removeFbclidCookie, undefined);
        }
        setConsent(parsed);
    }, [applyConsentToAll, denyConsentToAll, removeFunctionalCookies, setConsent]);
    useEffect(() => {
        isMounted.current = true;
        processConsent();
        // Listen for user updates
        const handleUpdate = () => {
            processConsent();
        };
        document.addEventListener("cookieyes_consent_update", handleUpdate);
        return () => {
            isMounted.current = false;
            document.removeEventListener("cookieyes_consent_update", handleUpdate);
        };
    }, [processConsent]);
    const hasConsented = (category) => {
        if (!consent) {
            return false;
        }
        return consent[category] === "yes";
    };
    return { consent, hasConsented };
}
